import { useEffect, useState } from "react";
import { ZeroProvider as ZeroReactProvider } from "@rocicorp/zero/react";
import { createMutators, schema, type Mutators } from "@lovd/zero-schema";
import { Toaster } from "@/components/ui/sonner";
import { useSession } from "@/features/auth/use-session";
import { env } from "@/lib/env";

/**
 * Composes the per-user providers under the auth gate. Renders nothing
 * until we have both a session and a fresh JWT for Zero.
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user?.id) {
      setToken(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await fetch(`${env.apiUrl}/auth/token`, { credentials: "include" });
      if (!res.ok) return;
      const { token: t } = (await res.json()) as { token: string };
      if (!cancelled) setToken(t);
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  if (!session?.user?.id || !token) {
    return (
      <div className="grid min-h-svh place-items-center text-sm text-muted-foreground">
        Connecting…
      </div>
    );
  }

  return (
    <ZeroReactProvider<typeof schema, Mutators>
      server={env.zeroUrl}
      userID={session.user.id}
      auth={token}
      schema={schema}
      mutators={createMutators({ sub: session.user.id })}
      kvStore="idb"
    >
      {children}
      <Toaster richColors theme="dark" />
    </ZeroReactProvider>
  );
}
